import React, { ComponentType } from 'react';
import {
  Scheduler,
  DayView,
  WeekView,
  Appointments,
} from '@devexpress/dx-react-scheduler-material-ui';
import { ViewState } from '@devexpress/dx-react-scheduler';
import useOrganizationStartWeekDay from 'hooks/useOrganizationStartWeekDay';
import useScrollCalendar from 'hooks/useScrollCalendar';
import TimeScaleTickCellComponent from 'components/BookProperties/BookingForm/MinutesBookingScheduler/WeekView/TimeScaleTickCellComponent/TimeScaleTickCellComponent';
import TimeScaleLabelComponent from 'components/ScheduleCalendar/libComponents/TimeScaleLabelComponent';
import {
  Divider,
  DialogContent,
  Stack,
  Box,
  FormControlLabel,
  Switch,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useUpdateScheduleMutation } from 'store/schedules/schedules';
import AddButton from 'common/AddButton/AddButton';
import TextButton from 'common/TextButton/TextButton';
import { daysInWeek } from 'helpers/timeConsts';
import { useAppDispatch } from 'store/hooks';
import { setErrorCatch, setSuccessStatus } from 'store/status/actions';
import QuestionCircleIcon from 'assets/icons/QuestionCircleIcon';
import TooltipIcon from 'common/TooltipIcon/TooltipIcon';
import DayScaleCellComponent from './libComponents/DayScaleCellComponent/DayScaleCellComponent';
import TimeTableCellComponent from './libComponents/TimeTableCellComponent/TimeTableCellComponent';
import ScheduleSummary from './ScheduleSummary/ScheduleSummary';
import useWorkScheduleCalendarContext from './context/useWorkScheduleCalendarContext';
import AppointmentContainerComponent from './libComponents/AppointmentContainerComponent';
import AppointmentContentComponent from './libComponents/AppointmentContentComponent/AppointmentConentComponent';
import { draftWorkScheduleId } from './context/WorkScheduleCalendarProvider';
import { WorkScheduleCalendarProps, WorkScheduleView } from './interfaces';
import { cellDuration, limitNumberOfDays } from './consts';

export default function WorkScheduleCalendar({
  handleCloseModal,
  onScheduleEdit,
}: WorkScheduleCalendarProps) {
  const { workSchedules, schedule, handleIncreaseScheduleDays } =
    useWorkScheduleCalendarContext();

  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [updateSchedule, { isLoading: scheduleLoading }] =
    useUpdateScheduleMutation();

  const isDraftScheduleExist = workSchedules.some(
    (workSchedule) => workSchedule.id === draftWorkScheduleId,
  );
  const schedulerWrapperRef = useScrollCalendar({});
  const startWeekDay = useOrganizationStartWeekDay();
  const weekIntervalCount = schedule.numberOfDays / daysInWeek;
  const disableRegularSwitch = schedule.numberOfDays % daysInWeek !== 0;

  const toggleIsRegularWeek = async () => {
    try {
      if (scheduleLoading) {
        return;
      }
      const updatedSchedule = await updateSchedule({
        ...schedule,
        regular: !schedule.regular,
      }).unwrap();
      if (onScheduleEdit) {
        onScheduleEdit(updatedSchedule);
      }
      dispatch(setSuccessStatus(true));
    } catch (error) {
      dispatch(setErrorCatch(error));
    }
  };

  return (
    <>
      <Stack
        direction="row"
        p={3}
        alignItems="center"
        justifyContent="space-between"
      >
        <FormControlLabel
          control={
            <Switch
              disabled={disableRegularSwitch}
              checked={schedule.regular}
              onChange={toggleIsRegularWeek}
            />
          }
          label={
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography>{t('project_tracking.regular_week')}</Typography>
              <TooltipIcon
                label={t(
                  schedule.regular
                    ? 'project_tracking.tooltip_regular'
                    : 'project_tracking.tooltip_irregular',
                )}
              >
                <QuestionCircleIcon
                  sx={{ fontSize: '2rem', color: 'icon.dark' }}
                />
              </TooltipIcon>
            </Stack>
          }
        />
        <TextButton
          onClick={() => handleIncreaseScheduleDays(schedule.regular ? 7 : 1)}
          label={
            schedule.regular
              ? 'project_tracking.add_week'
              : 'project_tracking.add_day'
          }
          iconPosition="start"
          disabled={schedule.numberOfDays >= limitNumberOfDays}
        />
      </Stack>
      <Divider />
      <Box
        height="calc(100vh - 40rem)"
        className="scheduler-wrapper"
        ref={schedulerWrapperRef}
      >
        <Scheduler
          data={workSchedules}
          firstDayOfWeek={startWeekDay}
          height="auto"
        >
          <ViewState
            currentViewName={
              schedule.regular ? WorkScheduleView.week : WorkScheduleView.day
            }
          />
          <WeekView
            dayScaleCellComponent={DayScaleCellComponent}
            timeTableCellComponent={TimeTableCellComponent}
            timeScaleTickCellComponent={TimeScaleTickCellComponent}
            timeScaleLabelComponent={TimeScaleLabelComponent}
            cellDuration={cellDuration}
            intervalCount={weekIntervalCount}
            name={WorkScheduleView.week}
          />
          <DayView
            dayScaleCellComponent={DayScaleCellComponent}
            timeTableCellComponent={TimeTableCellComponent}
            timeScaleTickCellComponent={TimeScaleTickCellComponent}
            timeScaleLabelComponent={TimeScaleLabelComponent}
            cellDuration={cellDuration}
            intervalCount={schedule.numberOfDays}
            name={WorkScheduleView.day}
          />
          <Appointments
            appointmentContentComponent={
              AppointmentContentComponent as ComponentType<Appointments.AppointmentContentProps>
            }
            containerComponent={
              AppointmentContainerComponent as ComponentType<Appointments.ContainerProps>
            }
          />
        </Scheduler>
      </Box>
      <Divider />
      <DialogContent>
        <ScheduleSummary />
      </DialogContent>
      <Divider />
      <DialogContent>
        <Stack direction="row" justifyContent="flex-end">
          <AddButton
            noIcon
            disabled={isDraftScheduleExist}
            onClick={handleCloseModal}
            label="label.done"
          />
        </Stack>
      </DialogContent>
    </>
  );
}
